import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Rectangle, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { getHeatColor } from './qualityHeatColors';
import { getFocusedZoom } from './qualityFactoryMapConfig';

const MIN_SIZE = 40;
const MIN_LABEL_SCALE = 0.35;
const MAX_LABEL_SCALE = 1.5;

function useLabelScale() {
  const map = useMap();
  const baseZoomRef = useRef(getFocusedZoom(map));
  const [zoom, setZoom] = useState(() => getFocusedZoom(map));
  useMapEvents({
    zoomend: () => setZoom(map.getZoom())
  });
  const raw = 2 ** (zoom - baseZoomRef.current);
  return Math.min(MAX_LABEL_SCALE, Math.max(MIN_LABEL_SCALE, raw));
}

function toBounds(zone) {
  return [
    [zone.y, zone.x],
    [zone.y + zone.h, zone.x + zone.w]
  ];
}

function zoneCountIcon(label, count, scale) {
  const nameFont = Math.max(9, Math.round(12 * scale));
  const countFont = Math.max(12, Math.round(17 * scale));
  const style = [
    'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
    'text-align:center', 'line-height:1.15', 'pointer-events:none',
    'color:#fff', 'text-shadow:0 1px 3px rgba(0,0,0,0.75)'
  ].join(';');
  const nameLine = label.replace(/ /g, '<br/>');
  return L.divIcon({
    className: 'quality-zone-stage-count-icon',
    html: `<div style="${style}"><span style="font-size:${nameFont}px;font-weight:600">${nameLine}</span><span style="font-size:${countFont}px;font-weight:800">${count}</span></div>`,
    iconSize: [Math.round(100 * scale), Math.round(44 * scale)],
    iconAnchor: [Math.round(50 * scale), Math.round(22 * scale)]
  });
}

function ZoneHeatDisplay({ zone, stages, maxCount, onStageClick, scale }) {
  const totalCount = stages.reduce((sum, stage) => sum + stage.count, 0);

  return (
    <>
      <Rectangle
        bounds={toBounds(zone)}
        pathOptions={{
          color: '#000000',
          weight: 1,
          fillColor: getHeatColor(totalCount, maxCount),
          fillOpacity: 0.85
        }}
        interactive
        eventHandlers={{
          click: () => onStageClick({ label: zone.label, names: stages.map(stage => stage.name) })
        }}
      />
      <Marker
        position={[zone.y + zone.h / 2, zone.x + zone.w / 2]}
        icon={zoneCountIcon(zone.label, totalCount, scale)}
        interactive={false}
      />
    </>
  );
}

ZoneHeatDisplay.propTypes = {
  zone: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    w: PropTypes.number.isRequired,
    h: PropTypes.number.isRequired
  }).isRequired,
  stages: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired
    })
  ).isRequired,
  maxCount: PropTypes.number.isRequired,
  onStageClick: PropTypes.func.isRequired,
  scale: PropTypes.number.isRequired
};

function labelIcon(label, editing, scale) {
  const fontSize = Math.max(10, Math.round(14 * scale));
  const style = [
    'display:flex', 'align-items:center', 'justify-content:center',
    'text-align:center', `font-size:${fontSize}px`, 'font-weight:600', 'color:#000',
    'white-space:pre-line', 'line-height:1.15', 'padding:2px',
    editing ? 'cursor:move' : 'pointer-events:none'
  ].join(';');
  return L.divIcon({
    className: editing ? 'quality-zone-label-icon is-editing' : 'quality-zone-label-icon',
    html: `<div style="${style}">${label.replace(/ /g, '<br/>')}</div>`,
    iconSize: [Math.round(90 * scale), Math.round(36 * scale)],
    iconAnchor: [Math.round(45 * scale), Math.round(18 * scale)]
  });
}

const resizeIcon = L.divIcon({
  className: 'quality-zone-resize-icon',
  html: '<div style="width:12px;height:12px;background:#2c7be5;border:2px solid #fff;border-radius:2px;cursor:nwse-resize;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6]
});

function EditableZone({ zone, editing, onChange, onCommit, scale }) {
  const map = useMap();
  const dragRef = useRef(null);
  const rectDragRef = useRef(null);
  const zoneRef = useRef(zone);
  const onChangeRef = useRef(onChange);
  const onCommitRef = useRef(onCommit);
  zoneRef.current = zone;
  onChangeRef.current = onChange;
  onCommitRef.current = onCommit;

  const handleMoveDragStart = useCallback(e => {
    dragRef.current = { startLatLng: e.target.getLatLng(), origX: zone.x, origY: zone.y };
  }, [zone.x, zone.y]);

  const computeMoveRect = useCallback(e => {
    const { startLatLng, origX, origY } = dragRef.current;
    const cur = e.target.getLatLng();
    const dx = cur.lng - startLatLng.lng;
    const dy = cur.lat - startLatLng.lat;
    return { ...zone, x: origX + dx, y: origY + dy };
  }, [zone]);

  const handleMoveDrag = useCallback(e => {
    if (!dragRef.current) return;
    onChange(zone.id, computeMoveRect(e));
  }, [zone.id, onChange, computeMoveRect]);

  const handleMoveDragEnd = useCallback(e => {
    if (!dragRef.current) return;
    onCommit(zone.id, computeMoveRect(e));
    dragRef.current = null;
  }, [zone.id, onCommit, computeMoveRect]);

  const handleRectMouseMove = useCallback(e => {
    if (!rectDragRef.current) return;
    const { startLatLng, origX, origY, id } = rectDragRef.current;
    const dx = e.latlng.lng - startLatLng.lng;
    const dy = e.latlng.lat - startLatLng.lat;
    onChangeRef.current(id, { ...zoneRef.current, x: origX + dx, y: origY + dy });
  }, []);

  const handleRectMouseUp = useCallback(e => {
    if (!rectDragRef.current) return;
    const { startLatLng, origX, origY, id } = rectDragRef.current;
    const dx = e.latlng.lng - startLatLng.lng;
    const dy = e.latlng.lat - startLatLng.lat;
    onCommitRef.current(id, { ...zoneRef.current, x: origX + dx, y: origY + dy });
    rectDragRef.current = null;
    map.dragging.enable();
    map.off('mousemove', handleRectMouseMove);
    map.off('mouseup', handleRectMouseUp);
  }, [map, handleRectMouseMove]);

  const handleRectMouseDown = useCallback(e => {
    L.DomEvent.stopPropagation(e);
    e.originalEvent.preventDefault();
    rectDragRef.current = { startLatLng: e.latlng, origX: zone.x, origY: zone.y, id: zone.id };
    map.dragging.disable();
    map.on('mousemove', handleRectMouseMove);
    map.on('mouseup', handleRectMouseUp);
  }, [zone.x, zone.y, zone.id, map, handleRectMouseMove, handleRectMouseUp]);

  useEffect(() => () => {
    map.off('mousemove', handleRectMouseMove);
    map.off('mouseup', handleRectMouseUp);
  }, [map, handleRectMouseMove, handleRectMouseUp]);

  const computeResizeRect = useCallback(e => {
    const cur = e.target.getLatLng();
    const newW = Math.max(MIN_SIZE, cur.lng - zone.x);
    const newH = Math.max(MIN_SIZE, cur.lat - zone.y);
    return { ...zone, w: newW, h: newH };
  }, [zone]);

  const handleResizeDrag = useCallback(e => {
    onChange(zone.id, computeResizeRect(e));
  }, [zone.id, onChange, computeResizeRect]);

  const handleResizeDragEnd = useCallback(e => {
    onCommit(zone.id, computeResizeRect(e));
  }, [zone.id, onCommit, computeResizeRect]);

  const centerLat = zone.y + zone.h / 2;
  const centerLng = zone.x + zone.w / 2;

  return (
    <>
      <Rectangle
        bounds={toBounds(zone)}
        pathOptions={{
          color: '#000000',
          weight: editing ? 1.5 : 1,
          fillColor: '#ffffff',
          fillOpacity: 0.8,
          dashArray: editing ? '4 3' : null
        }}
        className="quality-zone-rect"
        interactive
        eventHandlers={editing ? { mousedown: handleRectMouseDown } : {}}
      />
      <Marker
        position={[centerLat, centerLng]}
        icon={labelIcon(zone.label, editing, scale)}
        draggable={editing}
        interactive={editing}
        eventHandlers={editing ? {
          dragstart: handleMoveDragStart,
          drag: handleMoveDrag,
          dragend: handleMoveDragEnd
        } : {}}
      />
      {editing && (
        <Marker
          position={[zone.y + zone.h, zone.x + zone.w]}
          icon={resizeIcon}
          draggable
          eventHandlers={{ drag: handleResizeDrag, dragend: handleResizeDragEnd }}
        />
      )}
    </>
  );
}

EditableZone.propTypes = {
  zone: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    w: PropTypes.number.isRequired,
    h: PropTypes.number.isRequired
  }).isRequired,
  editing: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  scale: PropTypes.number.isRequired
};

export default function QualityZonesLayer({ zones, editing, onZoneChange, onZoneCommit, stagesByZone, maxCount, onStageClick }) {
  const scale = useLabelScale();

  return (
    <>
      {zones.map(zone => {
        const stages = stagesByZone[zone.id];
        if (!editing && stages && stages.length > 0) {
          return (
            <ZoneHeatDisplay
              key={zone.id}
              zone={zone}
              stages={stages}
              maxCount={maxCount}
              onStageClick={onStageClick}
              scale={scale}
            />
          );
        }
        return (
          <EditableZone
            key={zone.id}
            zone={zone}
            editing={editing}
            onChange={onZoneChange}
            onCommit={onZoneCommit}
            scale={scale}
          />
        );
      })}
    </>
  );
}

QualityZonesLayer.propTypes = {
  zones: PropTypes.array.isRequired,
  editing: PropTypes.bool.isRequired,
  onZoneChange: PropTypes.func.isRequired,
  onZoneCommit: PropTypes.func.isRequired,
  stagesByZone: PropTypes.object,
  maxCount: PropTypes.number,
  onStageClick: PropTypes.func
};

QualityZonesLayer.defaultProps = {
  stagesByZone: {},
  maxCount: 0,
  onStageClick: () => {}
};
